/**
 * The single gate for every third-party script this app loads.
 *
 * Two rules, and there are no exceptions to either:
 *
 *  1. A vendor script loads only when its flag is ON *and* its key resolves to
 *     something real. A missing or placeholder key means "do not load" -- never
 *     "load with a placeholder". Loading with a placeholder is what produced the
 *     literal request to `manager.eu.smartlook.cloud/rec/log/DEFAULT_SMARTLOOK_KEY`.
 *  2. Nothing here reads `process.env[someVariable]`. webpack's DefinePlugin
 *     substitutes only literal `process.env.VUE_APP_X` member access, so every
 *     lookup below is written out in full in `RAW_ENV`.
 *
 * See docs/third-party-scripts.md.
 */
import { isAutomated } from '@/config/isAutomated';
/**
 * Hosts on which third-party scripts are ON unless a flag says otherwise.
 * Everything else -- localhost, the asd tunnel, preview hosts, a bare IP --
 * defaults to OFF.
 */
const DEFAULT_ON_HOSTS = ['asq.global', 'dev.asq.global'];
/**
 * Values that look like configuration but are not. `%%X%%` is the token the
 * docker entrypoint rewrites at container start; `DEFAULT_X` is what a
 * `${X:-DEFAULT_X}` shell expansion leaves behind when the variable is unset.
 */
const PLACEHOLDER = /^(%%.*%%|DEFAULT_[A-Z0-9_]*|undefined|null|none|changeme|xxx+)$/i;
/* Literal DefinePlugin lookups. Do not rewrite these as dynamic indexing. */
const RAW_ENV = {
    master: process.env.VUE_APP_ENABLE_THIRD_PARTY,
    adsenseEnabled: process.env.VUE_APP_ENABLE_ADSENSE,
    gtmEnabled: process.env.VUE_APP_ENABLE_GTM,
    smartlookEnabled: process.env.VUE_APP_ENABLE_SMARTLOOK,
    storyblokBridgeEnabled: process.env.VUE_APP_ENABLE_STORYBLOK_BRIDGE,
    usercentricsEnabled: process.env.VUE_APP_ENABLE_USERCENTRICS,
    adsenseKey: process.env.VUE_APP_ADSENSE_CLIENT,
    gtmKey: process.env.VUE_APP_GTM_ID,
    smartlookKey: process.env.VUE_APP_SMARTLOOK_KEY,
    usercentricsKey: process.env.VUE_APP_USERCENTRICS_SETTINGS_ID
};
const FLAG_BY_VENDOR = {
    adsense: RAW_ENV.adsenseEnabled,
    gtm: RAW_ENV.gtmEnabled,
    smartlook: RAW_ENV.smartlookEnabled,
    storyblokBridge: RAW_ENV.storyblokBridgeEnabled,
    usercentrics: RAW_ENV.usercentricsEnabled
};
/**
 * Read a boolean env flag. Returns `undefined` -- meaning "not configured,
 * inherit the default" -- for an unset, blank or placeholder value, so that
 * "unset" and "explicitly off" stay distinguishable.
 */
export const readFlag = (raw) => {
    if (typeof raw !== 'string')
        return undefined;
    const value = raw.trim();
    if (!value || PLACEHOLDER.test(value))
        return undefined;
    if (/^(1|true|yes|on|enabled)$/i.test(value))
        return true;
    if (/^(0|false|no|off|disabled)$/i.test(value))
        return false;
    return undefined;
};
/**
 * Read a vendor key. Returns `null` for anything that is not usable, including
 * an unsubstituted docker token, a `DEFAULT_*` shell leftover, or a value of
 * the wrong length when the vendor documents a fixed one.
 */
export const readKey = (raw, exactLength) => {
    if (typeof raw !== 'string')
        return null;
    const value = raw.trim();
    if (!value || PLACEHOLDER.test(value))
        return null;
    if (exactLength !== undefined && value.length !== exactLength)
        return null;
    return value;
};
/** The suite must never reach a vendor, whatever the flags say. */
const isTestRunner = isAutomated;
const isDefaultOnHost = () => typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    DEFAULT_ON_HOSTS.includes(window.location.hostname);
/** The master switch, with the production-origin default behind it. */
export const thirdPartyMasterEnabled = () => {
    if (isTestRunner())
        return false;
    const explicit = readFlag(RAW_ENV.master);
    return explicit === undefined ? isDefaultOnHost() : explicit;
};
/**
 * Is this vendor allowed to load? A per-vendor flag, when set to something
 * readable, overrides the master switch in both directions -- that is how you
 * turn on exactly one vendor locally without turning on the rest.
 */
export const isVendorEnabled = (vendor) => {
    if (isTestRunner())
        return false;
    const explicit = readFlag(FLAG_BY_VENDOR[vendor]);
    return explicit === undefined ? thirdPartyMasterEnabled() : explicit;
};
/** Resolved, validated keys. `null` means "not configured, do not load". */
export const thirdPartyKeys = {
    adsenseClient: () => readKey(RAW_ENV.adsenseKey),
    gtmId: () => readKey(RAW_ENV.gtmKey),
    // Smartlook project keys are exactly 40 characters; the SDK rejects anything
    // else at runtime with `"key" length must be 40 characters long`.
    smartlookKey: () => readKey(RAW_ENV.smartlookKey, 40),
    usercentricsSettingsId: () => readKey(RAW_ENV.usercentricsKey)
};
